/**
 * Frame Renderer Module
 *
 * Handles the frame-by-frame rendering loop for video export.
 * Manages backpressure, progress reporting, and statistics.
 * Extracted from exporter.js for better separation of concerns.
 */

import { getBackpressureThresholds, ENCODER_CONFIG } from './export-performance.js';
import { STATS_CONFIG, CHUNKING_CONFIG, VIDEO_CONFIG } from './export-config.js';
import { RESOURCE_CATEGORIES } from './resource-registry.js';

/**
 * Frame rendering context - holds all state for a single export session
 */
export class FrameRenderer {
  /**
   * @param {Object} options
   * @param {Object} options.visualizer - Visualizer instance
   * @param {VideoEncoder} options.videoEncoder - Video encoder
   * @param {number} options.fps - Frames per second
   * @param {string} options.contextType - 'webgpu', 'webgl', or '2d'
   * @param {boolean} options.useStreaming - Using FSA streaming mode
   * @param {Array} options.writeQueue - FSA write queue (if streaming)
   * @param {Function} options.onProgress - Progress callback (0-1)
   * @param {Function} options.onTrackChange - Track change callback
   * @param {Function} options.onStatsUpdate - Stats update callback
   * @param {Function} options.isAborted - Function to check abort status
   * @param {Function} options.getEncoderError - Function returning encoder error (if any)
   * @param {Object} options.encodedFrameCounter - Object with `count` property tracking encoded frames
   * @param {ResourceRegistry} options.resourceRegistry - Optional resource registry for tracking
   */
  constructor(options) {
    this.visualizer = options.visualizer;
    this.videoEncoder = options.videoEncoder;
    this.fps = options.fps;
    this.contextType = options.contextType || '2d';
    this.useStreaming = options.useStreaming || false;
    this.writeQueue = options.writeQueue || [];
    this.onProgress = options.onProgress;
    this.onTrackChange = options.onTrackChange;
    this.onStatsUpdate = options.onStatsUpdate;
    this.isAborted = options.isAborted || (() => false);
    this.getEncoderError = options.getEncoderError || (() => null);
    // Counter object for tracking encoded frames (incremented by encoder output callback)
    this.encodedFrameCounter = options.encodedFrameCounter || { count: 0 };
    // Optional resource registry for tracking WebCodecs resources
    this.resourceRegistry = options.resourceRegistry || null;

    // Initialize lastEncodedForLog for accurate enc fps calculation
    this.lastEncodedForLog = 0;

    // Backpressure configuration - use standard thresholds from export-performance.js
    // Note: Same logic applies regardless of lyrics - matches working state at 6eb6878
    const bpConfig = getBackpressureThresholds(this.contextType);
    this.hardQueue = bpConfig.hardQueue;
    this.softQueue = bpConfig.softQueue;
    this.useFlush = bpConfig.useFlush;

    // Frame timing
    this.frameDurationMicros = 1_000_000 / this.fps;
    this.keyframeInterval = VIDEO_CONFIG.keyframeIntervalSec * this.fps;

    // Statistics tracking
    this.resetStats();
  }

  /**
   * Reset statistics for a new logging window
   * Note: lastEncodedForLog is NOT reset here - it's updated in logStats()
   */
  resetStats() {
    this.totalDrawTime = 0;
    this.totalLyricsTime = 0;
    this.totalVideoFrameTime = 0;
    this.totalEncodeTime = 0;
    this.totalYieldTime = 0;
    this.totalFlushTime = 0;
    this.totalFrameTime = 0;
    this.maxEncodeQueue = 0;
    this.framesSinceLog = 0;
    this.framesSinceCoopYield = 0;
    this.lastLogTime = performance.now();
    // Don't reset lastEncodedForLog - it needs to persist across windows

    // Granular yield tracking
    this.hardYieldTime = 0;
    this.hardYieldCount = 0;
    this.softYieldTime = 0;
    this.softYieldCount = 0;
    this.coopYieldTime = 0;
    this.coopYieldCount = 0;
    this.maxQueueBeforeBackpressure = 0;
  }

  /**
   * Render all frames from the frequency stream
   * @param {Object} freqStream - Frequency stream from AudioAnalyzer
   * @param {Object} options
   * @param {OffscreenCanvas} options.canvas - Main render canvas
   * @param {Object} options.compositor - LyricsCompositor instance (optional)
   * @param {Array} options.trackTimeline - Track timeline for progress
   * @param {number} options.estimatedFrameCount - Estimated total frames
   * @returns {Promise<{frameCount: number, aborted: boolean}>}
   */
  async renderAllFrames(freqStream, options) {
    const {
      canvas,
      compositor,
      trackTimeline,
      estimatedFrameCount,
    } = options;

    let actualFrameCount = estimatedFrameCount || 0;
    let currentTrackIdx = -1;
    let frameIndex = 0;

    const frameIterator = freqStream.frames[Symbol.asyncIterator]();

    console.log(`[FrameRenderer] Starting frame loop, frameCount≈${actualFrameCount}`);

    while (true) {
      const next = await frameIterator.next();
      if (next.done) break;

      const frequencyFrame = next.value;

      // Check for abort
      if (this.isAborted()) {
        console.log('[FrameRenderer] Abort requested, stopping...');
        return { frameCount: frameIndex, aborted: true };
      }

      // Check for encoder error (video or audio)
      const encoderError = this.getEncoderError();
      if (encoderError) {
        throw new Error(`Encoder error during export: ${encoderError.message || encoderError}`);
      }

      // Track change detection
      const frameTime = frequencyFrame.frameTime;
      for (let t = 0; t < trackTimeline.length; t++) {
        const track = trackTimeline[t];
        if (frameTime >= track.startTime && frameTime < track.endTime && t !== currentTrackIdx) {
          currentTrackIdx = t;
          if (this.onTrackChange) {
            this.onTrackChange(track.name, t + 1, trackTimeline.length);
          }
          break;
        }
      }

      const frameStart = performance.now();

      // Log stats periodically
      this.framesSinceLog++;
      const now = frameStart;
      if (now - this.lastLogTime >= STATS_CONFIG.logIntervalMs) {
        this.logStats(frameIndex, actualFrameCount);
        this.resetStats();
      }

      // For WebGPU with lyrics: set up overlay BEFORE draw so it's composited on GPU
      // Use frameTime from frequency frame (audio-aligned timing)
      if (compositor && this.contextType === 'webgpu' && this.visualizer.setLyricsOverlay) {
        const lyricsStart = performance.now();
        // Get lyrics canvas (just the lyrics, transparent background)
        const lyricsCanvas = compositor.getLyricsCanvas(frameTime);
        // Set as overlay - will be rendered on GPU after main scene
        this.visualizer.setLyricsOverlay(lyricsCanvas);
        this.totalLyricsTime += performance.now() - lyricsStart;
      }

      // Draw visualization (for WebGPU, this now includes lyrics overlay)
      // Pass enhanced audio features from the frequency stream (lazy-evaluated)
      const drawStart = performance.now();
      const drawResult = this.visualizer.draw(frequencyFrame.data, frequencyFrame.features);
      if (drawResult instanceof Promise) {
        await drawResult;
      }
      this.totalDrawTime += performance.now() - drawStart;

      // Determine frame source (with or without lyrics)
      // For WebGPU: lyrics already composited on GPU, use canvas directly
      // For 2D/WebGL: use CPU compositing via compositor.compositeFrame()
      let frameSource = canvas;
      if (compositor && this.contextType !== 'webgpu') {
        const lyricsStart = performance.now();
        frameSource = compositor.compositeFrame(canvas, frameTime);
        this.totalLyricsTime += performance.now() - lyricsStart;
      }

      // Create video frame
      const vfStart = performance.now();
      const timestamp = frameIndex * this.frameDurationMicros;

      // Defensive check (should be caught in exporter.js, but belt-and-suspenders)
      if (!('VideoFrame' in window)) {
        throw new Error('VideoFrame API not available');
      }

      const videoFrame = new VideoFrame(frameSource, {
        timestamp,
        duration: this.frameDurationMicros,
        alpha: 'discard',
      });

      // Track VideoFrame if registry provided (for debugging resource leaks)
      let frameId = null;
      if (this.resourceRegistry) {
        frameId = this.resourceRegistry.track(videoFrame, {
          name: `VideoFrame#${frameIndex}`,
          type: 'VideoFrame',
          category: RESOURCE_CATEGORIES.WEBCODECS_FRAME,
        }).__resId;
      }
      this.totalVideoFrameTime += performance.now() - vfStart;

      // Apply backpressure
      await this.applyBackpressure();

      // Encode frame
      const encodeStart = performance.now();
      const isKeyframe = frameIndex % this.keyframeInterval === 0;
      this.videoEncoder.encode(videoFrame, { keyFrame: isKeyframe });
      videoFrame.close();

      // Untrack after close (frame is already disposed)
      if (this.resourceRegistry && frameId !== null) {
        this.resourceRegistry.untrack(frameId);
      }
      this.totalEncodeTime += performance.now() - encodeStart;
      this.maxEncodeQueue = Math.max(this.maxEncodeQueue, this.videoEncoder.encodeQueueSize);

      // Progress update
      if (frameIndex % 10 === 0 && this.onProgress) {
        const denom = Math.max(actualFrameCount || 0, frameIndex + 1);
        const progressFrames = denom ? (frameIndex / denom) : 0;
        // 20-90% for video encoding
        this.onProgress(0.2 + progressFrames * 0.7);
      }

      // Cooperative yield for UI responsiveness
      this.framesSinceCoopYield++;
      if (this.framesSinceCoopYield >= ENCODER_CONFIG.coopYieldInterval) {
        const coopStart = performance.now();
        await new Promise(resolve => setTimeout(resolve, 1));
        const elapsed = performance.now() - coopStart;
        this.totalYieldTime += elapsed;
        this.coopYieldTime += elapsed;
        this.coopYieldCount++;
        this.framesSinceCoopYield = 0;
      }

      // Flush FSA write queue periodically
      if (this.useStreaming && this.writeQueue.length > CHUNKING_CONFIG.fsaWriteQueueMax) {
        const flushStart = performance.now();
        await Promise.all(this.writeQueue);
        this.writeQueue.length = 0;
        this.totalFlushTime += performance.now() - flushStart;
      }

      this.totalFrameTime += performance.now() - frameStart;
      frameIndex++;
    }

    console.log(`[FrameRenderer] Frame loop complete, rendered ${frameIndex} frames`);
    return { frameCount: frameIndex, aborted: false };
  }

  /**
   * Apply backpressure based on encoder queue size
   * Uses thresholds from export-performance.js - same logic for all context types
   */
  async applyBackpressure() {
    let queueSize = this.videoEncoder.encodeQueueSize;
    this.maxQueueBeforeBackpressure = Math.max(this.maxQueueBeforeBackpressure, queueSize);

    // Loop until the encoder queue is back under control. This prevents runaway
    // queues (hundreds of frames) that stall the encoder and tank fps.
    while (queueSize > this.softQueue) {
      const isHard = queueSize > this.hardQueue;
      const waitStart = performance.now();

      // Only flush when explicitly allowed and queue is far above the hard limit;
      // otherwise do short cooperative sleeps to let the encoder drain.
      if (this.useFlush && queueSize > this.hardQueue * 2) {
        await this.videoEncoder.flush();
      } else {
        const delay = isHard ? 2 : 1; // slightly longer pause for hard backpressure
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      const elapsed = performance.now() - waitStart;
      this.totalYieldTime += elapsed;

      if (isHard) {
        this.hardYieldTime += elapsed;
        this.hardYieldCount++;
      } else {
        this.softYieldTime += elapsed;
        this.softYieldCount++;
      }

      queueSize = this.videoEncoder.encodeQueueSize;
      this.maxQueueBeforeBackpressure = Math.max(this.maxQueueBeforeBackpressure, queueSize);

      // Safety: if the encoder is completely stalled, don't spin forever.
      if (elapsed > 200 && queueSize > this.hardQueue * 3) {
        console.warn('[FrameRenderer] Backpressure loop: encoder not draining as expected');
        break;
      }
    }
  }

  /**
   * Log frame statistics
   */
  logStats(frameIndex, totalFrames) {
    const now = performance.now();
    const secondsWindow = (now - this.lastLogTime) / 1000;
    const fps = this.framesSinceLog / Math.max(secondsWindow, 0.001);

    // Calculate encoded fps from the counter (incremented by encoder output callback)
    const currentEncoded = this.encodedFrameCounter.count;
    const encFps = (currentEncoded - this.lastEncodedForLog) / Math.max(secondsWindow, 0.001);

    const avgDraw = this.totalDrawTime / Math.max(1, this.framesSinceLog);
    const avgLyrics = this.totalLyricsTime / Math.max(1, this.framesSinceLog);
    const avgVF = this.totalVideoFrameTime / Math.max(1, this.framesSinceLog);
    const avgEncode = this.totalEncodeTime / Math.max(1, this.framesSinceLog);
    const avgYield = this.totalYieldTime / Math.max(1, this.framesSinceLog);
    const avgFlush = this.totalFlushTime / Math.max(1, this.framesSinceLog);
    const avgFrame = this.totalFrameTime / Math.max(1, this.framesSinceLog);

    const totalFramesForLog = Math.max(totalFrames || 0, frameIndex + 1) || 'unknown';

    // Include lyrics time if significant (> 0.1ms)
    const lyricsInfo = avgLyrics > 0.1 ? `, lyrics: ${avgLyrics.toFixed(1)}ms` : '';

    // Build yield breakdown string
    const yieldBreakdown = [];
    if (this.hardYieldCount > 0) {
      yieldBreakdown.push(`hard:${this.hardYieldTime.toFixed(0)}ms(${this.hardYieldCount}x)`);
    }
    if (this.softYieldCount > 0) {
      yieldBreakdown.push(`soft:${this.softYieldTime.toFixed(0)}ms(${this.softYieldCount}x)`);
    }
    if (this.coopYieldCount > 0) {
      yieldBreakdown.push(`coop:${this.coopYieldTime.toFixed(0)}ms(${this.coopYieldCount}x)`);
    }
    const yieldDetail = yieldBreakdown.length > 0 ? ` [${yieldBreakdown.join(', ')}]` : '';

    console.log(
      `[FrameRenderer] Frame ${frameIndex}/${totalFramesForLog} ` +
      `(${fps.toFixed(1)} fps | enc ${encFps.toFixed(1)} fps) | ` +
      `frame: ${avgFrame.toFixed(1)}ms, draw: ${avgDraw.toFixed(1)}ms${lyricsInfo}, ` +
      `vf: ${avgVF.toFixed(1)}ms, yield: ${avgYield.toFixed(1)}ms${yieldDetail}, ` +
      `encQ: ${this.videoEncoder.encodeQueueSize}/${this.maxQueueBeforeBackpressure}, encQmax: ${this.maxEncodeQueue}`
    );

    // Report to UI
    if (this.onStatsUpdate) {
      const framesRemaining = totalFrames ? Math.max(0, totalFrames - frameIndex) : 0;
      const etaSeconds = framesRemaining ? framesRemaining / fps : 0;
      this.onStatsUpdate(fps, etaSeconds, frameIndex, totalFrames || 0);
    }

    this.lastEncodedForLog = currentEncoded;
  }
}

/**
 * Build track timeline for progress reporting
 * @param {Array} items - Playlist items with duration
 * @returns {Array} Timeline entries with startTime, endTime, name, index
 */
export function buildTrackTimeline(items) {
  const timeline = [];
  let cumulativeTime = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item.included) continue;

    const dur = item.duration || 0;
    timeline.push({
      name: item.name,
      startTime: cumulativeTime,
      endTime: cumulativeTime + dur,
      index: i,
    });
    cumulativeTime += dur;
  }

  return timeline;
}
